/**
 * Market Card Carousel Handler
 * Manages market card carousel initialization on the state search results page.
 * Follows the same pattern as builderCardCarousel for top markets sections.
 */
import Carousel from '@components/carousel/dynamicCarousel';

/** Tracks active Carousel instances keyed by their data-market-card-carousel identifier. */
const carouselInstances = new Map();

/**
 * Initializes (or re-initializes) a carousel for a market card section.
 * If a Carousel instance already exists for the given identifier, it is
 * destroyed first so that stale IntersectionObservers and RxJS subscriptions
 * from the previous DOM tree are cleaned up before new ones are created.
 *
 * The item list element is checked before proceeding: if it is absent in the
 * current DOM (e.g. the injected HTML does not contain [data-item-list]) the
 * function returns early without storing an instance, which keeps destroy()
 * safe to call on every stored instance without touching dynamicCarousel.js.
 *
 * @param {string} carouselQuerySelector - The CSS selector for the carousel container element.
 * @param {string} identifier - The data-market-card-carousel attribute value used as the instance key.
 */
const setMarketCardCarousel = (carouselQuerySelector, identifier) => {
  // Always destroy any existing instance first so stale IntersectionObservers
  // and RxJS subscriptions are cleaned up, even when the new DOM no longer
  // contains [data-item-list] (e.g. the carousel content was cleared).
  const existing = carouselInstances.get(identifier);
  if (existing) {
    existing.destroy();
    carouselInstances.delete(identifier);
  }

  // Guard: only create and store a new instance when the item list is present.
  // This ensures setIntersectionObserver() will always create this.intersectionObserver.
  const itemListElement = document.querySelector(`${carouselQuerySelector} [data-item-list]`);
  if (!itemListElement) {
    return;
  }

  const carouselSettings = {
    itemListQuerySelector: `${carouselQuerySelector} [data-item-list]`,
    previousQuerySelector: `${carouselQuerySelector} [data-left-arrow]`,
    nextQuerySelector: `${carouselQuerySelector} [data-right-arrow]`,
  };

  const carousel = new Carousel();
  carousel.setSettings(carouselSettings);
  carousel.create();
  carouselInstances.set(identifier, carousel);
};

/**
 * Initializes all market card carousels on the page.
 * Searches for elements with the data-market-card-carousel attribute
 * and initializes a carousel for each one.
 */
const initializeMarketCardCarousels = () => {
  const marketCarouselElements = document.querySelectorAll('[data-market-card-carousel]');

  marketCarouselElements.forEach((carouselElement) => {
    const carouselIdentifier = carouselElement.getAttribute('data-market-card-carousel');
    if (!carouselIdentifier) {
      return;
    }

    const carouselQuerySelector = `[data-market-card-carousel="${carouselIdentifier}"]`;
    setMarketCardCarousel(carouselQuerySelector, carouselIdentifier);
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMarketCardCarousels);
} else {
  initializeMarketCardCarousels();
}

export default initializeMarketCardCarousels;
